import { WAWI_BESTAND_FILTER, WAWI_FILIALEN_STANDARD, type Me, type ModulFreischaltung, type WawiBestandFilter, type WawiFilialBestand } from '@portal/contracts';

/**
 * Warenwirtschaft (#863): reine Hilfsfunktionen der Team-Seite (Routen, Filter, Anzeige, Rechte) — getestet ohne DOM.
 * Modul-Schlüssel/Pfad bleiben `lager` / `/lager` (#820). Rechte prüft immer die API; die Oberfläche blendet nur aus.
 */
export const WAWI_PFAD = '/lager';

export type WawiRoute =
  | { seite: 'artikel' }
  | { seite: 'detail'; nr: number }
  | { seite: 'warengruppen' }
  | { seite: 'sync' }
  | { seite: 'unbekannt' };

export const UNTERNAVIGATION = [
  { seite: 'artikel', label: 'Artikel', pfad: WAWI_PFAD },
  { seite: 'warengruppen', label: 'Warengruppen', pfad: `${WAWI_PFAD}/warengruppen` },
  { seite: 'sync', label: 'Sync', pfad: `${WAWI_PFAD}/sync` },
] as const;

export function wawiRoute(pathname: string): WawiRoute {
  const p = pathname.replace(/\/+$/, '') || '/';
  if (p === WAWI_PFAD) return { seite: 'artikel' };
  if (p === `${WAWI_PFAD}/warengruppen`) return { seite: 'warengruppen' };
  if (p === `${WAWI_PFAD}/sync`) return { seite: 'sync' };
  const m = /^\/lager\/artikel\/([1-9]\d{0,9})$/.exec(p);
  if (m) return { seite: 'detail', nr: Number(m[1]) };
  return { seite: 'unbekannt' };
}

export function aktiverReiter(r: WawiRoute): 'artikel' | 'warengruppen' | 'sync' | null {
  if (r.seite === 'detail') return 'artikel';
  return r.seite === 'unbekannt' ? null : r.seite;
}

export const artikelPfad = (nr: number) => `${WAWI_PFAD}/artikel/${nr}`;

type MitModulen = Pick<Me, 'modules'> | { modules?: ModulFreischaltung };
export const darfVerwalten = (me: MitModulen): boolean => me.modules?.lager === 'verwalten';

export interface WawiFilter {
  suche: string;
  filiale: string;
  ober: string;
  wg: string;
  /** „Nicht zugeordnet“ (Warengruppe IS NULL) — Query `zuordnung=ohne`, schließt ober/wg aus (Review 221) */
  ohneZuordnung: boolean;
  bestand: WawiBestandFilter;
  seite: number;
}

/** Filter aus der URL-Query (Teilen/Zurück); Unbekanntes fällt auf den Standard „Bestand ≠ 0“ zurück */
export function filterAusQuery(search: string): WawiFilter {
  const q = new URLSearchParams(search);
  const b = q.get('bestand') ?? '';
  const seite = Number(q.get('seite') ?? 1);
  const ohneZuordnung = q.get('zuordnung') === 'ohne';
  return {
    suche: q.get('suche') ?? '',
    filiale: /^\d{1,5}$/.test(q.get('filiale') ?? '') ? q.get('filiale')! : '',
    ober: ohneZuordnung ? '' : q.get('ober') ?? '',
    wg: ohneZuordnung ? '' : q.get('wg') ?? '',
    ohneZuordnung,
    bestand: (WAWI_BESTAND_FILTER as readonly string[]).includes(b) ? (b as WawiBestandFilter) : 'ungleich0',
    seite: Number.isInteger(seite) && seite > 0 ? seite : 1,
  };
}

export function filterZuQuery(f: WawiFilter): string {
  const q = new URLSearchParams();
  if (f.suche.trim()) q.set('suche', f.suche.trim());
  if (f.filiale) q.set('filiale', f.filiale);
  if (f.ohneZuordnung) q.set('zuordnung', 'ohne');
  else {
    if (f.ober) q.set('ober', f.ober);
    if (f.wg) q.set('wg', f.wg);
  }
  if (f.bestand !== 'ungleich0') q.set('bestand', f.bestand);
  if (f.seite > 1) q.set('seite', String(f.seite));
  const s = q.toString();
  return s ? `?${s}` : '';
}

export const BESTAND_LABEL: Record<WawiBestandFilter, string> = {
  ungleich0: 'Bestand ≠ 0',
  alle: 'Alle geführten',
  kein: 'Kein Bestand',
  negativ: 'Minusbestand',
};

/** Spalten der Tabelle: Filialen 1/2/3 immer, weitere (z. B. 999) zusätzlich */
export function filialSpalten(filialen: readonly number[]): number[] {
  return [...new Set([...WAWI_FILIALEN_STANDARD, ...filialen])].sort((a, b) => a - b);
}

export const filialLabel = (nr: number) => (nr === 999 ? 'Filiale 999 (Sonder)' : `Filiale ${nr}`);
export const filialKurz = (nr: number) => (nr === 999 ? '999*' : `F${nr}`);

/** Bestand einer Filiale: Zahl bzw. „—“ ohne Datensatz; Minus mit echtem Minuszeichen */
export function bestandIn(bestaende: readonly WawiFilialBestand[], filiale: number): { text: string; negativ: boolean } {
  const b = bestaende.find((x) => x.filialNr === filiale);
  if (!b) return { text: '—', negativ: false };
  return { text: zahl(b.bestand), negativ: b.bestand < 0 };
}

const ZAHL = new Intl.NumberFormat('de-DE');
export const zahl = (n: number) => (n < 0 ? `−${ZAHL.format(-n)}` : ZAHL.format(n));

/** Warengruppe als „Ober › Gruppe“ bzw. „Nicht zugeordnet“ */
export function warengruppeAnzeige(a: { oberName: string | null; oberNr: string | null; wgName: string | null; wgNr: string | null }): string {
  if (!a.oberNr) return 'Nicht zugeordnet';
  return `${a.oberName ?? a.oberNr} › ${a.wgName ?? a.wgNr ?? ''}`;
}

/** Ziel eines Klicks im Warengruppen-Baum: Artikelliste gefiltert auf die Gruppe; null-Gruppe → `zuordnung=ohne` (Review 221 P2) */
export function warengruppeFilterPfad(oberNr: string | null, wgNr: string | null): string {
  const basis: WawiFilter = { suche: '', filiale: '', ober: '', wg: '', ohneZuordnung: false, bestand: 'alle', seite: 1 };
  const f: WawiFilter = oberNr === null ? { ...basis, ohneZuordnung: true } : { ...basis, ober: oberNr, wg: wgNr ?? '' };
  return `${WAWI_PFAD}${filterZuQuery(f)}`;
}

/** Parameter der Artikel-API aus dem UI-Filter (nur gesetzte Werte) */
export function artikelQuery(f: WawiFilter, groesse = 50): Record<string, string | number | undefined> {
  return {
    suche: f.suche || undefined, filiale: f.filiale || undefined, bestand: f.bestand, seite: f.seite, groesse,
    ...(f.ohneZuordnung ? { zuordnung: 'ohne' } : { ober: f.ober || undefined, wg: f.wg || undefined }),
  };
}

export const PHASE_LABEL = { bereit: 'bereit', laeuft: 'Lauf aktiv', unvollstaendig: 'Unvollständig – Snapshot nicht veröffentlicht', fehler: 'Fehler' } as const;
